import { useCallback, useState } from "react";
import { parseDocument } from "htmlparser2";
import { filter, findAll, findOne, getAttributeValue } from "domutils";
import { convertNodeToReactElement } from "@hedgedoc/html-to-react";
import Icon from "../Icon";
import classNames from "classnames";
import { isLocaleNumeric, localeParseFloat } from "../../utils/NumberUtils";

const CockpitTable = ({
  className = "",
  style,
  content,
  theme = "white",
  zebra = false,
  responsive = false,
  hasHeader = false,
  sortable = false
}) => {
  const ASC = 0;
  const DESC = 1;
  const [sortConfig, setSortConfig] = useState({ col: 0, dir: ASC });

  //console.debug("CockpitTable content", content);
  const dom = parseDocument(content);
  const tableRoot = findOne(elm => elm.name === "table", dom.children);
  const rowNodes = findAll(elm => elm.name === "tr", tableRoot.children);
  //console.debug("CockpitTable rowNodes", rowNodes);
  const rows = rowNodes.map(rowElm => ({
    cells: findAll(elm => elm.name === "td", rowElm.children).map(cellElm => ({
      value: filter(elm => elm.type === "text", cellElm.children, true)
        .map(elm => elm.data)
        .join(""),
      children: cellElm.children.map((elm, i) =>
        convertNodeToReactElement(elm, i)
      ),
      style: getAttributeValue(cellElm, "style")
    })),
    style: getAttributeValue(rowElm, "style")
  }));
  rows.forEach(row => {
    row.key = row.cells.map(c => c.value).join(" ");
  });

  //console.debug("rows", rows);
  const headers = hasHeader && rows[0]?.cells.map(c => c.children);
  //console.debug("headers", headers);

  const getSortDir = useCallback(
    col => {
      let dir = ASC;
      if (sortConfig?.col === col && sortConfig?.dir === ASC) {
        dir = DESC;
      }
      return dir;
    },
    [sortConfig]
  );

  const sortedRows =
    sortable && sortConfig?.col !== undefined
      ? [
          ...rows.splice(hasHeader ? 1 : 0).sort((a, b) => {
            const aValue = a.cells[sortConfig.col]?.value;
            const bValue = b.cells[sortConfig.col]?.value;

            if (isLocaleNumeric(aValue) && isLocaleNumeric(bValue)) {
              return sortConfig.dir === DESC
                ? localeParseFloat(bValue) - localeParseFloat(aValue)
                : localeParseFloat(aValue) - localeParseFloat(bValue);
            }
            if (isLocaleNumeric(aValue)) {
              return sortConfig.dir === DESC ? 1 : -1;
            }
            if (isLocaleNumeric(bValue)) {
              return sortConfig.dir === DESC ? -1 : 1;
            }
            return sortConfig.dir === DESC
              ? bValue.localeCompare(aValue)
              : aValue.localeCompare(bValue);
          })
        ]
      : rows.splice(hasHeader ? 1 : 0);

  //console.debug("CockpitTable sortedRows", sortedRows);

  return tableRoot ? (
    <table
      className={classNames(
        "cockpit-table",
        "theme-" + theme,
        { zebra, responsive },
        className
      )}
      style={style}
    >
      <tbody>
        {hasHeader && (
          <tr key="head" className="head">
            {headers.map((children, ic) => (
              <th
                key={ic}
                onClick={() =>
                  sortable && setSortConfig({ col: ic, dir: getSortDir(ic) })
                }
                className={
                  sortable
                    ? "sortable hint-sort-" +
                      (getSortDir(ic) === DESC ? "desc" : "asc")
                    : undefined
                }
              >
                {children}{" "}
                {sortable &&
                  sortConfig.col === ic &&
                  (sortConfig.dir === DESC ? (
                    <Icon name="chevron-down" />
                  ) : (
                    <Icon name="chevron-up" />
                  ))}
              </th>
            ))}
          </tr>
        )}
        {sortedRows.map(row => (
          <tr key={row.key}>
            {row.cells.map((cell, ic) => (
              <td key={ic} className={classNames({ "row-header": ic === 0 })}>
                {hasHeader && ic > 0 && (
                  <span className="cell-label" key="label">
                    {headers?.[ic]}
                  </span>
                )}
                {cell.children}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  ) : (
    <div className="empty-msg">
      <div>
        <Icon name="face-sad-tear" size="4x" />
      </div>
      <div>The table is empty.</div>
    </div>
  );
};

export default CockpitTable;
